<template>
  <CardBase :headerBorder="false">
    <template #header>{{ $route.meta.title }}</template>
    <template #toolbar>
      <div class="flex mt-2 md:mt-0">
        <router-link
          v-if="userCan('create.member')"
          :to="{ name: 'MemberCreate' }"
          class="button button--primary poppins"
        >
          <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />New
          Member
        </router-link>
      </div>
    </template>
    <template #content>
      <form class="form form-search" method="get" @submit.prevent="reloadTable">
        <div class="form-search__row">
          <div class="form-search__single">
            <label class="form__label">Search</label>
            <input
              type="text"
              class="form__input"
              placeholder="Member Name / Panel ID / Username / Bank Account Holder or Number / Contact Info"
              v-model="formData.search"
            />
          </div>
        </div>
        <div class="form-search__row">
          <div class="form-search__double">
            <label class="form__label">Asset</label>
            <multiselect
              v-model="formData.asset"
              :options="listAsset"
              :loading="listAssetLoading"
              :close-on-select="true"
              :hide-selected="false"
              track-by="id"
              label="name"
              :placeholder="formData.asset ? formData.asset.name : 'All Asset'"
              :deselectLabel="'Remove selection'"
              :selectLabel="'Select this option'"
              @input="
                formData.asset_id = formData.asset ? formData.asset.id : ''
              "
            >
              <template #noResult>No results found.</template>
            </multiselect>
          </div>
        </div>

        <div class="form-search__single">
          <FormNote class="mb-6"
            >Note: Jika Asset dipilih, yang dicari hanya akan berdasarkan Panel
            ID / Username.</FormNote
          >
        </div>

        <div class="form-search__action">
          <div class="w-full flex">
            <BaseButton type="submit" buttonClass="secondary"
              >Search</BaseButton
            >
            <BaseButton
              class="mx-2"
              type="button"
              buttonClass="default"
              @click="reset"
              >Reset Search</BaseButton
            >
          </div>
        </div>
      </form>

      <vue-good-table
        :key="tableKey"
        mode="remote"
        theme="polar-bear"
        :rows="tableData.rows"
        :columns="tableData.columns"
        :total-rows="tableData.totalRows"
        :isLoading="tableData.isLoading"
        :sort-options="{
          enabled: false,
        }"
        :pagination-options="{
          enabled: true,
          perPageDropdown: perPageDropdown,
          dropdownAllowAll: false,
          setCurrentPage: tableData.currentPage,
          infoFn: tablePageInfo,
        }"
        styleClass="vgt-table striped bordered"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <template v-if="props.column.field == 'name'">
            {{ props.row.name }}
            <br />
            <span class="block mt-2"
              ><strong class="block">UUID:</strong>{{ props.row.uuid }}</span
            >
          </template>
          <template v-else-if="props.column.field == 'contacts'">
            <template v-if="props.row.contacts.length > 0">
              <span
                class="block"
                v-for="contact in props.row.contacts"
                :key="contact.id"
              >
                {{ contact.tooltip }}
              </span>
            </template>
            <template v-else>-</template>
          </template>
          <template v-else-if="props.column.field == 'bank_accounts'">
            <template v-if="props.row.bank_accounts.length > 0">
              <span
                class="flex justify-between items-start mb-2"
                v-for="bank in props.row.bank_accounts"
                :key="bank.id"
              >
                <span class="block">
                  <router-link
                    v-if="userCan('update.member-bank-account')"
                    :to="{
                      name: 'MemberBankEdit',
                      params: { memberId: props.row.uuid, id: bank.uuid },
                    }"
                    class="link"
                    v-tooltip="'Edit ' + bank.tooltip"
                    >{{ bank.tooltip }}
                  </router-link>
                  <span v-else>{{ bank.tooltip }}</span>
                </span>

                <span class="flex justify-end w-16">
                  <router-link
                    :to="{
                      name: 'MemberBankDelete',
                      params: { memberId: props.row.uuid, id: bank.uuid },
                    }"
                    class="hover:text-blue-600"
                    v-if="userCan('delete.member-bank-account')"
                    v-tooltip="'Delete ' + bank.tooltip"
                  >
                    <font-awesome-icon :icon="['far', 'trash-alt']" />
                  </router-link>
                  <router-link
                    :to="{
                      name: 'MemberBankTransfer',
                      params: { memberId: props.row.uuid, id: bank.uuid },
                    }"
                    class="hover:text-blue-600 ml-2"
                    v-if="userCan('transfer.member') && bank.uuid"
                    v-tooltip="'Transfer ' + bank.tooltip"
                  >
                    <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                  </router-link>
                </span>
              </span>
            </template>
            <template v-else>-</template>
          </template>
          <template v-else-if="props.column.field == 'accounts'">
            <template v-if="props.row.accounts.length > 0">
              <span
                class="flex justify-between items-start"
                v-for="account in props.row.accounts"
                :key="account.id"
              >
                <span class="block">
                  <router-link
                    v-if="userCan('update.member-account') && account.uuid"
                    :to="{
                      name: 'AccountEdit',
                      params: { memberId: props.row.uuid, id: account.uuid },
                    }"
                    class="link"
                    v-tooltip="'Edit ' + account.tooltip"
                  >
                    {{ account.tooltip }}
                  </router-link>
                  <span v-else>{{ account.tooltip }}</span>
                  <span class="block mt-1 mb-2"
                    ><font-awesome-icon :icon="['fas', 'coins']" class="mr-1" />
                    <template v-if="account.balance">{{
                      account.balance
                    }}</template
                    ><template v-else>0.00</template></span
                  >
                </span>
                <span class="flex justify-end w-16">
                  <router-link
                    :to="{
                      name: 'AccountDelete',
                      params: { memberId: props.row.uuid, id: account.uuid },
                    }"
                    class="hover:text-blue-600"
                    v-if="userCan('delete.member-account') && account.uuid"
                    v-tooltip="'Delete ' + account.tooltip"
                  >
                    <font-awesome-icon :icon="['far', 'trash-alt']" />
                  </router-link>
                  <router-link
                    :to="{
                      name: 'AccountTransfer',
                      params: { memberId: props.row.uuid, id: account.uuid },
                    }"
                    class="hover:text-blue-600 ml-2"
                    v-if="userCan('transfer.member') && account.uuid"
                    v-tooltip="'Transfer ' + account.tooltip"
                  >
                    <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                  </router-link>
                </span>
              </span>
            </template>
            <template v-else>-</template>
          </template>
          <span v-else-if="props.column.field == 'uuid'">
            <router-link
              v-if="
                userCan('merge.member-bank-account') &&
                props.row.bank_accounts.length > 1
              "
              :to="{
                name: 'MemberBankMerge',
                params: { memberId: props.row.uuid },
              }"
              v-tooltip="'Merge Member Bank Account for ' + props.row.name"
              class="table-action__link"
            >
              <font-awesome-icon
                flip="horizontal"
                :icon="['fas', 'project-diagram']"
              />
            </router-link>
            <router-link
              v-if="userCan('create.member-account')"
              :to="{
                name: 'AccountCreate',
                params: { memberId: props.row.uuid },
              }"
              v-tooltip="'Create Account for ' + props.row.name"
              class="table-action__link mx-2"
            >
              <font-awesome-icon :icon="['fas', 'user']" />
            </router-link>
            <router-link
              v-if="userCan('create.member-bank-account')"
              :to="{
                name: 'MemberBankCreate',
                params: { memberId: props.row.uuid },
              }"
              v-tooltip="'Create Bank Account for ' + props.row.name"
              class="table-action__link mx-2"
            >
              <font-awesome-icon :icon="['fas', 'wallet']" />
            </router-link>
            <router-link
              :to="{
                name: 'MemberTransactionHistory',
                params: { memberId: props.row.uuid },
              }"
              v-tooltip="'View Transaction History for ' + props.row.name"
              class="table-action__link mx-2"
              ><font-awesome-icon :icon="['fas', 'search-dollar']"
            /></router-link>
            <router-link
              v-if="userCan('create.member')"
              :to="{ name: 'MemberEdit', params: { id: props.row.uuid } }"
              v-tooltip="'Edit ' + props.row.name"
              class="table-action__link mx-2"
            >
              <font-awesome-icon :icon="['fas', 'edit']" />
            </router-link>
          </span>
        </template>
        <!-- <div slot="emptystate" v-if="!$route.query.search" class="text-center text-gray-600 text-sm">Please search member to see results.</div> -->
      </vue-good-table>
    </template>
  </CardBase>
</template>

<script>
import { showListMixin } from "@/mixins/showListMixin";
import { getListAssetMixin } from "@/mixins/getListAssetMixin";

export default {
  mixins: [showListMixin, getListAssetMixin],
  data() {
    return {
      tableData: {
        columns: [
          {
            label: "Name",
            field: "name",
          },
          {
            label: "Contact Info",
            field: "contacts",
          },
          {
            label: "Bank Accounts",
            field: "bank_accounts",
          },
          {
            label: "Account IDs",
            field: "accounts",
          },
          {
            label: "",
            field: "uuid",
            thClass: "vgt-table__action",
            tdClass: "vgt-table__action",
          },
        ],
        sort_by: "name",
        sort_dir: "asc",
      },
      formData: {
        search: "",
        asset: null,
        asset_id: "",
      },
    };
  },
  created() {
    this.getListAsset();
  },
  watch: {
    $route() {
      const queryParams = this.$route.query;

      if (queryParams.asset_id != undefined) {
        this.listAsset.forEach((data) => {
          if (data.id == queryParams.asset_id) {
            this.formData.asset = data;
            this.formData.asset_id = data.id;
          }
        });
      }
    },
  },
  methods: {
    deleteMemberBank(memberId, id, name) {
      this.$swal({
        title: "Delete " + name,
        text: "Are you sure?",
        icon: "warning",
        showCancelButton: true,
        reverseButtons: true,
      }).then((res) => {
        if (res.value) {
          this.$store.commit("toggleLoading");
          this.$http
            .delete(
              process.env.VUE_APP_API_URL +
                this.$route.path +
                "/" +
                memberId +
                "/bank-account/" +
                id
            )
            .then((res) => {
              this.$store.commit("toggleLoading");
              if (res.status == 200) {
                this.$swal({
                  title: "All done!",
                  text: res.data.message,
                  icon: "success",
                }).then(() => {
                  this.loadTableData();
                });
              }
            })
            .catch((res) => {
              if (res.response.status == 409) {
                this.$store.commit("toggleLoading");
                this.$swal({
                  title: "Whoops!",
                  text: res.response.data.data.member_bank_account,
                  icon: "error",
                });
              }
            })
            .finally(() => {});
        }
      });
    },
    deleteMemberAccount(memberId, id, name) {
      this.$swal({
        title: "Delete " + name,
        text: "Are you sure?",
        icon: "warning",
        showCancelButton: true,
        reverseButtons: true,
      }).then((res) => {
        if (res.value) {
          this.$store.commit("toggleLoading");
          this.$http
            .delete(
              process.env.VUE_APP_API_URL +
                this.$route.path +
                "/" +
                memberId +
                "/account/" +
                id
            )
            .then((res) => {
              this.$store.commit("toggleLoading");
              if (res.status == 200) {
                this.$swal({
                  title: "All done!",
                  text: res.data.message,
                  icon: "success",
                }).then(() => {
                  this.loadTableData();
                });
              }
            })
            .catch((res) => {
              if (res.response.status == 409) {
                this.$store.commit("toggleLoading");
                this.$swal({
                  title: "Whoops!",
                  text: res.response.data.data.member_account,
                  icon: "error",
                });
              }
            })
            .finally(() => {});
        }
      });
    },
    reset() {
      this.formData.search = "";
      this.formData.asset = null;
      this.formData.asset_id = "";
      this.tableData.limit = 10;

      if (this.$route.query.search == undefined) {
        this.tableKey++;
      } else {
        this.tableData.currentPage = 1;
        this.tableData.rows = [];
        this.$router.push({ name: "MemberList" }).catch(() => {});
      }
    },
    reloadTable() {
      if (!this.formData.search || this.formData.search.length < 3) {
        this.$swal({
          position: "bottom-end",
          showConfirmButton: false,
          toast: true,
          timer: 3000,
          timerProgressBar: true,
          title: "Search query must have at least 3 characters",
          icon: "warning",
        });
      } else {
        this.tableData.currentPage = 1;
        this.$router
          .replace({
            query: {
              search: this.formData.search,
              asset_id: this.formData.asset_id,
              tz: new Date().getTime(),
            },
          })
          .catch(() => {});
      }
    },
    loadTableData() {
      const queryParams = this.$route.query;
      let searchParam = "";
      this.tableData.isLoading = false;

      if (queryParams.search != undefined) {
        this.formData = { ...this.formData, ...this.$route.query };
        for (const key in queryParams) {
          searchParam += `&${key}=${queryParams[key]}`;
        }

        this.tableData.isLoading = true;
        this.$http
          .get(
            process.env.VUE_APP_API_URL +
              `${this.$route.path}?page=${this.tableData.currentPage}&limit=${this.tableData.limit}&sort_by=${this.tableData.sort_by}&sort_dir=${this.tableData.sort_dir}${searchParam}`
          )
          .then((res) => {
            this.tableData.rows = res.data.data;
            this.tableData.totalRows = res.data.total;
          })
          .finally(() => {
            this.tableData.isLoading = false;
          });
      } else {
        this.tableData.rows = [];
        this.tableData.totalRows = 0;
      }
      // } else {
      //   this.tableData.isLoading = false;
      // }
    },
  },
};
</script>
